import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Menu } from "lucide-react";
import { BUSINESS } from "@/data/site";
import { trackEvent } from "@/lib/api";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";

const NAV = [
  { to: "/services", label: "Services" },
  { to: "/safety-screening", label: "Safety" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/pricing", label: "Pricing" },
  { to: "/service-areas", label: "Areas" },
  { to: "/reviews", label: "Reviews" },
  { to: "/about", label: "About" },
  { to: "/faqs", label: "FAQs" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <div className="sticky top-3 z-50 px-3 sm:px-4" data-testid="site-header">
      <header className="container-mbf bg-white/85 backdrop-blur-md border border-[#F1E6D0] rounded-[1.75rem] shadow-soft">
        <div className="flex items-center justify-between h-[96px] px-4 sm:px-5 gap-4">
          <Link to="/" className="flex items-center shrink-0" data-testid="header-logo-link">
            <img
              src="/logo-color.svg"
              alt="Mom's Best Friend Sitters"
              className="h-[72px] md:h-20 w-auto"
            />
          </Link>

          <nav className="hidden xl:flex items-center gap-5" aria-label="Primary">
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`link-nav ${location.pathname.startsWith(item.to) ? "!text-coral" : ""}`}
                data-testid={`nav-${item.to.replace(/\//g, "")}-link`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2.5">
            <a
              href={BUSINESS.phoneHref}
              onClick={() => trackEvent("call_click", { source: "header" })}
              className="hidden lg:flex items-center gap-2 text-sm font-bold text-espresso hover:text-coral transition-colors"
              data-testid="header-phone-link"
            >
              <span className="w-8 h-8 rounded-full bg-tinted flex items-center justify-center"><Phone className="w-4 h-4 text-gold" /></span>
              {BUSINESS.phone}
            </a>
            <Link
              to="/book"
              onClick={() => trackEvent("cta_click", { source: "header" })}
              className="btn-accent hidden sm:inline-flex !py-2.5 !px-5"
              data-testid="header-request-cta"
            >
              Request a Sitter
            </Link>

            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button className="xl:hidden w-10 h-10 rounded-full bg-tinted flex items-center justify-center text-espresso" aria-label="Open menu" data-testid="mobile-menu-trigger">
                  <Menu className="w-5 h-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-parchment border-l border-[#F1E6D0] w-[300px] p-0">
                <div className="flex items-center p-5 border-b border-[#F1E6D0]">
                  <SheetTitle className="font-display text-lg text-espresso font-bold">Menu</SheetTitle>
                  <SheetDescription className="sr-only">Site navigation menu</SheetDescription>
                </div>
                <nav className="flex flex-col p-5 gap-1" aria-label="Mobile">
                  {NAV.map((item) => (
                    <SheetClose asChild key={item.to}>
                      <Link to={item.to} className="py-3 px-4 rounded-2xl text-espresso font-semibold hover:bg-tinted transition-colors" data-testid={`mobile-nav-${item.to.replace(/\//g, "")}-link`}>
                        {item.label}
                      </Link>
                    </SheetClose>
                  ))}
                  <div className="mt-4 flex flex-col gap-3">
                    <a href={BUSINESS.phoneHref} className="btn-outline w-full" data-testid="mobile-phone-link"><Phone className="w-4 h-4" /> {BUSINESS.phone}</a>
                    <SheetClose asChild>
                      <Link to="/book" className="btn-accent w-full" data-testid="mobile-request-cta">Request a Sitter</Link>
                    </SheetClose>
                  </div>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>
    </div>
  );
}
